import type {
  MessageChannel,
  NoteDetails,
  SmsKeywordType,
  ReplyTarget
} from "../types";
import { ManualContent } from "./ManualContent";
import { EmailContent } from "./EmailContent";
import { SmsContent } from "./SmsContent";
import { ChatContent } from "./ChatContent";
import { CallTranscriptContent } from "./CallTranscriptContent";
import { VoiceContent } from "./VoiceContent";
import { type Attachment, type VoiceDetails } from "../types";
import type { MessageTranslation } from "../hooks/useMessageTranslation";

interface MessageAddress {
  name: string;
  address: string;
}

interface MessageContentProps {
  channel: MessageChannel;
  isFromContact: boolean;
  body: string;
  subject?: string;
  isReply?: boolean;
  sentViaQuotaFallback?: boolean;
  from?: MessageAddress;
  htmlBody?: string;
  to?: MessageAddress;
  toAddresses?: string[];
  ccAddresses?: string[];
  bccAddresses?: string[];
  noteType?: string;
  noteDetails?: NoteDetails;
  messageId?: string;
  onReply?: (target: ReplyTarget) => void;
  attachments?: Attachment[];
  sentAsSms?: boolean;
  sentAsSmsTo?: string | null;
  sentAsSmsFrom?: string | null;
  smsFailedReason?: string | null;
  receivedViaSms?: boolean;
  smsKeywordType?: SmsKeywordType | null;
  className?: string;
  voiceDetails?: VoiceDetails | null;
  // Voice-only: reopen the make-a-call modal to redial, and drop the call row.
  onVoiceRetry?: () => void;
  onDelete?: () => void;
  // Voice-only: inbound call rendering (CRM-905).
  isInbound?: boolean;
  translation: MessageTranslation;
  translateInBubble: boolean;
}

export const MessageContent = ({
  channel,
  isFromContact,
  body,
  htmlBody,
  to,
  toAddresses,
  ccAddresses,
  bccAddresses,
  noteType,
  noteDetails,
  subject,
  isReply,
  sentViaQuotaFallback,
  from,
  messageId,
  onReply,
  attachments,
  sentAsSms,
  sentAsSmsTo,
  sentAsSmsFrom,
  smsFailedReason,
  receivedViaSms,
  smsKeywordType,
  className,
  voiceDetails,
  onVoiceRetry,
  onDelete,
  isInbound,
  translation,
  translateInBubble
}: MessageContentProps) => {
  // A softphone call is one self-contained card, not a text bubble — it
  // carries its own From/To rows, outcome, recording and transcript.
  if (channel === "voice" && voiceDetails)
    return (
      <VoiceContent
        isFromContact={isFromContact}
        voiceDetails={voiceDetails}
        from={from}
        to={to}
        contactName={to?.name}
        onRedial={onVoiceRetry}
        onDelete={onDelete}
        className={className}
        isInbound={isInbound}
      />
    );

  if (channel === "note" && noteDetails?.type === "call_transcript")
    return (
      <CallTranscriptContent
        noteDetails={noteDetails}
        contactName={to?.name}
        isFromContact={isFromContact}
        isInbound={isInbound}
      />
    );

  if (channel === "note")
    return (
      <ManualContent
        isFromContact={isFromContact}
        body={body}
        htmlBody={htmlBody}
        noteType={noteType}
        contactName={to?.name}
      />
    );

  if (channel === "chat")
    return (
      <ChatContent
        isFromContact={isFromContact}
        body={body}
        sentAsSms={sentAsSms}
        sentAsSmsTo={sentAsSmsTo}
        sentAsSmsFrom={sentAsSmsFrom}
        smsFailedReason={smsFailedReason}
        receivedViaSms={receivedViaSms}
        attachments={attachments}
        className={className}
        translation={translation}
        translateInBubble={translateInBubble}
      />
    );

  if (channel === "email")
    return (
      <EmailContent
        isFromContact={isFromContact}
        body={body}
        bodyHtml={htmlBody}
        subject={subject}
        isReply={isReply}
        sentViaQuotaFallback={sentViaQuotaFallback}
        from={from}
        to={to}
        toAddresses={toAddresses}
        ccAddresses={ccAddresses}
        bccAddresses={bccAddresses}
        messageId={messageId}
        onReply={onReply}
        attachments={attachments}
        className={className}
        translation={translation}
        translateInBubble={translateInBubble}
      />
    );

  return (
    <SmsContent
      isFromContact={isFromContact}
      body={body}
      from={from}
      to={to}
      attachments={attachments}
      smsKeywordType={smsKeywordType}
      className={className}
      translation={translation}
    />
  );
};
